<template>
  <div class="main user-layout-register">
    <h3><span>注册</span></h3>
    <a-form ref="formRef" :model="form" :rules="rules">
      <a-alert
        v-if="isRegisterError"
        type="error"
        show-icon
        style="margin-bottom: 24px"
        :message="registerErrorInfo"
        closable
        @after-close="handleCloseRegisterError"
      />
      <a-form-item name="username">
        <a-input v-model:value="form.username" size="large" autocomplete="off" placeholder="账户" />
      </a-form-item>
      <a-form-item name="password" has-feedback>
        <a-input-password
          v-model:value="form.password"
          size="large"
          autocomplete="off"
          placeholder="密码"
          :maxLength="20"
        />
      </a-form-item>
      <a-form-item name="confirmPassword" has-feedback>
        <a-input-password
          v-model:value="form.confirmPassword"
          size="large"
          autocomplete="off"
          placeholder="确认密码"
          :maxLength="20"
        />
      </a-form-item>
      <a-row :gutter="16" v-if="captchaEnabled">
        <a-col class="gutter-row" :span="16">
          <a-form-item name="code">
            <a-input
              v-model:value="form.code"
              size="large"
              type="text"
              autocomplete="off"
              placeholder="验证码"
            >
              <template #prefix>
                <SecurityScanOutlined style="color: rgba(0,0,0,.25)" />
              </template>
            </a-input>
          </a-form-item>
        </a-col>
        <a-col class="gutter-row" :span="8">
          <img class="getCaptcha" :src="codeUrl" @click="getCode" />
        </a-col>
      </a-row>
      <a-form-item>
        <a-button
          size="large"
          type="primary"
          htmlType="submit"
          class="register-button"
          :loading="registering"
          @click.stop.prevent="handleRegister"
          :disabled="registering"
        >
          注册
        </a-button>
        <router-link class="login" :to="{ name: 'login' }">使用已有账户登录</router-link>
      </a-form-item>
    </a-form>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { notification } from 'ant-design-vue'
import { SecurityScanOutlined } from '@ant-design/icons-vue'
import { getCodeImg, register } from '@/api/login'

const router = useRouter()
const formRef = ref()

const form = reactive({
  username: undefined,
  password: undefined,
  confirmPassword: undefined,
  code: undefined,
  uuid: undefined,
})

const rules = {
  username: [
    { required: true, trigger: 'blur', message: '请输入您的账号' },
    {
      min: 2,
      max: 20,
      message: '用户账号长度必须介于 2 和 20 之间',
      trigger: 'blur',
    },
  ],
  password: [
    { required: true, trigger: 'blur', message: '请输入您的密码' },
    { required: true, validator: validateNewPass, trigger: 'blur' },
  ],
  confirmPassword: [
    { required: true, trigger: 'blur', message: '请再次输入您的密码' },
    { required: true, validator: validateConfirmPass, trigger: 'blur' },
  ],
  code: [{ required: true, trigger: 'change', message: '请输入验证码' }],
}

const codeUrl = ref('')
const isRegisterError = ref(false)
const registerErrorInfo = ref('')
const registerBtn = ref(false)
const registering = ref(false)
const captchaEnabled = ref(true)

function validateNewPass(rule, value) {
  if (value === '') {
    return Promise.reject(new Error('请输入新密码'))
  }
  if (
    !/^(?![\d]+$)(?![a-zA-Z]+$)(?![^\da-zA-Z]+$)([^\u4e00-\u9fa5\s]){5,20}$/.test(value)
  ) {
    return Promise.reject(
      new Error('请输入5-20位英文字母、数字或者符号（除空格），且字母、数字和标点符号至少包含两种')
    )
  }
  if (form.confirmPassword !== '') {
    formRef.value?.validateFields(['confirmPassword'])
  }
  return Promise.resolve()
}

function validateConfirmPass(rule, value) {
  if (value === '') {
    return Promise.reject(new Error('请再次输入新密码确认'))
  }
  if (value !== form.password) {
    return Promise.reject(new Error('两次输入的密码不一致'))
  }
  return Promise.resolve()
}

getCode()

function getCode() {
  getCodeImg().then((res) => {
    captchaEnabled.value = res.captchaEnabled === undefined ? true : res.captchaEnabled
    if (captchaEnabled.value) {
      codeUrl.value = 'data:image/gif;base64,' + res.img
      form.uuid = res.uuid
    }
  })
}

function handleRegister() {
  formRef.value
    ?.validate()
    .then(() => {
      registering.value = true
      register(form)
        .then((res) => registerSuccess(res))
        .catch((err) => requestFailed(err))
        .finally(() => {
          registering.value = false
        })
    })
    .catch(() => {
      setTimeout(() => {
        registering.value = false
      }, 600)
    })
}

function registerSuccess(res) {
  router.push({ path: '/login' })
  const username = form.username
  setTimeout(() => {
    notification.success({
      message: '恭喜你，您的账号 ' + username + ' 注册成功！',
    })
  }, 1000)
  handleCloseRegisterError()
}

function requestFailed(err) {
  isRegisterError.value = true
  registerErrorInfo.value = err
  form.code = undefined
  if (captchaEnabled.value) {
    getCode()
  }
}

function handleCloseRegisterError() {
  isRegisterError.value = false
  registerErrorInfo.value = ''
}
</script>

<style lang="less">
.user-register {
  &.error {
    color: #ff0000;
  }
  &.warning {
    color: #ff7e05;
  }
  &.success {
    color: #52c41a;
  }
}
.user-layout-register {
  .ant-input-group-addon:first-child {
    background-color: #fff;
  }
}
</style>

<style lang="less" scoped>
.user-layout-register {
  & > h3 {
    font-size: 16px;
    margin-bottom: 20px;
  }
  .getCaptcha {
    display: block;
    width: 100%;
    height: 40px;
  }
  .register-button {
    width: 50%;
  }
  .login {
    float: right;
    line-height: 40px;
  }
}
</style>
